// Iter-158.13.2.17b — Project Switcher entry builder.
//
// Iter-158.13.2.20f.32 — SPLIT by access class.
//   .17b originally collapsed Hub + dept rows for the same
//   project into a single switcher entry (Hub wins). That was the
//   right shape when the switcher was one flat list. Now that the
//   dropdown mirrors MyProjects's two-class information hierarchy
//   (PROJECTS vs PRODUCTION MANAGEMENT (SCRIPT CHANGES)), the same
//   production must be able to appear in BOTH lists when the user
//   holds both accesses — same story on both surfaces.
//
// Pure, side-effect-free helper that partitions the two canonical
// project-list sources into three ordered lists the header
// switcher renders directly:
//
//   • legacyItems              — legacy `/api/projects` rows,
//                                one per project, kept under a
//                                "Legacy" sub-label inside PROJECTS
//                                so the migration surface stays
//                                accessible without inventing a new
//                                section for it.
//   • projectItems             — beta workspaces the caller holds
//                                DEPARTMENT-level access on
//                                (`is_hub === false`). Collapsed to
//                                first-seen dept row per project_id
//                                so multi-department members still
//                                get one entry per production. The
//                                row's own `landing_url` is used
//                                verbatim (already resolved
//                                server-side).
//   • productionManagementItems — beta workspaces the caller holds
//                                PRODUCTION-scope authority on
//                                (`is_hub === true`). One entry
//                                per production. Landing_url lands
//                                on the Production Hub.
//
// Rules preserved from .17b:
//   • Archived workspaces are excluded regardless of API caller.
//   • Rows without `landing_url` are dropped (never render a menu
//     item that navigates nowhere).
//   • Legacy items get `landing_url = "/projects/{id}"` here so
//     ProjectHeader stays dumb about legacy routing.
//   • Malformed / null inputs never crash.
export function buildSwitcherEntries(legacyProjects, betaWorkspaces) {
    const legacyItems = (Array.isArray(legacyProjects) ? legacyProjects : [])
        .filter((p) => p && p.id)
        .map((p) => ({
            kind: "legacy",
            id: p.id,
            name: p.name || "",
            landing_url: `/projects/${p.id}`,
        }));

    const wsList = Array.isArray(betaWorkspaces) ? betaWorkspaces : [];

    // Preserve first-seen ordering per project_id within each
    // class (Map iterates insertion order).
    const projByPid = new Map();
    const hubByPid = new Map();
    for (const w of wsList) {
        if (!w || !w.project_id) continue;
        if (w.archived) continue;
        if (typeof w.landing_url !== "string" || w.landing_url.length === 0) {
            continue;
        }
        if (w.is_hub === true) {
            if (!hubByPid.has(w.project_id)) hubByPid.set(w.project_id, w);
        } else {
            if (!projByPid.has(w.project_id)) projByPid.set(w.project_id, w);
        }
    }

    const shape = (kind) => (w) => ({
        kind,
        id: w.project_id,
        name: w.project_name || "",
        landing_url: w.landing_url,
    });

    const projectItems = Array.from(projByPid.values()).map(shape("beta"));
    const productionManagementItems = Array.from(hubByPid.values()).map(
        shape("beta-hub"),
    );

    return { legacyItems, projectItems, productionManagementItems };
}
